import {
  Component,
  OnInit,
  Input,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnDestroy
} from '@angular/core';
import { BookmarkModel } from 'src/app/bookmarks/models/bookmark.model';
import { PublishService } from '../../publish.service';
import { CameraService } from 'src/app/engine/camera.service';
import { CameraModel } from 'src/app/engine/models/camera.model';
import { Observable, Subject, interval, Subscription } from 'rxjs';
import { map, takeUntil, filter, tap } from 'rxjs/operators';
import { WidgetOptions } from '../../models/widget-options.model';
import { animationFrame } from 'rxjs/internal/scheduler/animationFrame';
import { ScreenDimensions } from 'src/app/shared/models/screen.dimensions.model';
import { BackgroundModel } from 'src/app/engine/models/background.model';
import { MessageEnum } from 'src/app/widget-api/models/message.enum';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { UiIO } from 'src/app/widget-api/models/ui.io';
import { LoaderTypeEnum } from 'src/app/view/models/loader-type.enum';
import { ScrollModeEnum } from 'src/app/engine/models/scroll-mode.enum';

declare const HumanAPI: any;

const scaleSmoothFactor = 0.95;

@Component({
  selector: 'app-publish-widget',
  templateUrl: './publish-widget.component.html',
  styleUrls: ['./publish-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PublishWidgetComponent implements OnInit, OnDestroy {
  @ViewChild('widgetContainer', { static: true })
  containerRef: ElementRef;

  @ViewChild('widgetFrame', { static: true })
  frameRef: ElementRef;

  @Input()
  model: BookmarkModel;

  @Input()
  width: number;

  @Input()
  height: number;

  @Input()
  scalable = false;

  @Input()
  set enableUI(value: boolean) {
    this._enableUI = value;
    this.setWidgetUIConfig(this._enableUI, this._params);
  }

  @Input()
  set bgColor(value: BackgroundModel) {
    this._bgColor = value;
    if (this._ready) {
      this.renderWidgetBGColor();
    }
  }

  @Input()
  set widgetParams(value: WidgetOptions) {
    this._params = value;
    this._rotateDegrees = value ? value.loadRotate : 0;
    this.setWidgetUIConfig(this._enableUI, this._params);
  }

  @Output()
  cameraUpdated = new EventEmitter<CameraModel>();

  private ngUnsubscribe = new Subject();

  get topOffset(): number {
    const containerHeight = this.elRef.nativeElement.offsetHeight;
    const targetOffset = (containerHeight - this.height) / 2;
    return this.scaleFactor < 1 ? targetOffset : Math.max(0, targetOffset);
  }

  get leftOffset(): number {
    const containerWidth = this.elRef.nativeElement.offsetWidth;
    const targetOffset = (containerWidth - this.width) / 2;
    return this.scaleFactor < 1 ? targetOffset : Math.max(0, targetOffset);
  }

  get scaleFactor(): number {
    if (!this.scalable) {
      return 1;
    } else {
      const { offsetWidth, offsetHeight } = this.containerRef.nativeElement;
      const xScale = offsetWidth / this.width;
      const yScale = offsetHeight / this.height;
      const targetScale = Math.min(xScale, yScale, 1);

      if (targetScale === 1) { // default scale
        return 1;
      } else { // scaled model: smooth
        return targetScale * scaleSmoothFactor;
      }
    }
  }

  private _apiRef: any;
  private _params: WidgetOptions;
  private _defaultCamera: CameraModel;
  private _currentCamera: CameraModel;

  private _defaultColor: BackgroundModel;
  private _bgColor: BackgroundModel;
  private _rotateDegrees: number;
  private _ready = false;
  private _enableUI = true;

  constructor(
    private elRef: ElementRef,
    private publishService: PublishService,
    private cameraService: CameraService) {
  }

  ngOnInit() {
    const frameId = this.frameRef.nativeElement.id;
    this._apiRef = new HumanAPI({ iframeId: frameId });

    // listen for API ready state
    this.getAPIEvent(EventsEnum.HumanReady)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {

        // ready state
        this._ready = true;

        this.setupCameraTracking();
        this.setupCameraRotation();
        this.setWidgetUIConfig(this._enableUI, this._params);

        // setup background color
        this.getCurrentBGColor()
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe((color) => {
            this._defaultColor = color;
            // initial bg color
            this.renderWidgetBGColor();
          });
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  setWidgetUIConfig(enableUI: boolean, widgetOptions: WidgetOptions) {
      // API ready
    if (this._apiRef && widgetOptions) {

      if (enableUI) { // set UI form user selection
        const uiDisplay = this.convertWidgetOptionsToDisplayParams(widgetOptions);
        this.resetDisplay();
        this.setUIDisplay(uiDisplay);

        // set loading experience
        const loadingExperience = this.getLoadingExperience(widgetOptions.initial);
        const scrollCaptureMode = this.getScrollCaptureMode(widgetOptions.disableScroll);

        this._apiRef.send(MessageEnum.UISetLoadingExperience, loadingExperience);
        this._apiRef.send(MessageEnum.UISetScrollCaptureMode, scrollCaptureMode);

      } else { // disable UI + loading experience
        this.hideUIDisplay();

        const loadingExperience = this.getLoadingExperience({ button: false, handHint: false, play: false });
        const scrollCaptureMode = this.getScrollCaptureMode({ alert: false, overlay: false });

        this._apiRef.send(MessageEnum.UISetLoadingExperience, loadingExperience);
        this._apiRef.send(MessageEnum.UISetScrollCaptureMode, scrollCaptureMode);
      }
    }
  }

  takeSnapshot(dimensions?: ScreenDimensions): Observable<string> {
    return this.getAPIData(MessageEnum.UISnapshot, {
      width: this.width,
      height: this.height,
      ...dimensions
    });
  }

  /**
   * Orbits the camera around the model by specified degrees.
   */
  private setCameraOrbit(degrees: number) {
    this.sendAPIData(MessageEnum.CameraOrbit, {
      yaw: degrees
    });
  }

  /**
 * Sets UI options (i.e., fullscreen, help, etc...)
 */

  private renderWidgetBGColor() {
    this.setWidgetBGColor(this._bgColor || this._defaultColor);
  }

  private setWidgetBGColor(background: BackgroundModel) {
    if (this._apiRef && background) {
      this.sendAPIData(MessageEnum.UISetBackground, background);
    }
  }

  private setupCameraTracking() {

    // get initial camera
    this.getCurrentCamera()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((currentCamera) => {

        // set fixed default camera
        this.setDefaultCamera(currentCamera);

        // begin tracking camera changes
        this.getCameraUpdates()
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe((updateCamera) => {
            this.setUpdateCamera(updateCamera);
          });

      });
  }

  // use API to rotate widget fixed amount in an interval
  private setupCameraRotation() {
    // setup camera rotate animation
    let timeLast = performance.now();
    interval(0, animationFrame)
      .pipe(
        filter(() => !!this._rotateDegrees), // on valid rotation values
        map(() => this._rotateDegrees * (performance.now() - timeLast) * 0.001),
        tap(() => timeLast = performance.now()),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((degrees) => {
        this.setCameraOrbit(degrees);
      });
  }

  private setDefaultCamera(camera: CameraModel) {
    this._defaultCamera = camera;
    this.setUpdateCamera(camera);
  }

  private setUpdateCamera(camera: CameraModel) {
    this._currentCamera = camera;

    // #TODO: compare cameras, smooth ???
    const camerasMatch = this.cameraService.compareCameras(
      this.cameraService.roundCamera(this._defaultCamera, 3),
      this.cameraService.roundCamera(this._currentCamera, 3)
    );

    // dispatch changes: validate delta from original
    const targetCamera = !camerasMatch ? this._currentCamera : null;
    this.cameraUpdated.emit(targetCamera);
  }

  private getAPIEvent(event: EventsEnum): Observable<any> {
    return new Observable<any>((observer) => {
      this._apiRef.on(event, (data: any) => {
        observer.next(data);
      });
    });
  }

  private getAPIData(message: MessageEnum, payload?: any): Observable<any> {
    return new Observable<any>((observer) => {
      this._apiRef.send(message, payload, (data: any) => {
        observer.next(data);
        observer.complete();
      });
    });
  }

  private sendAPIData(message: MessageEnum, payload?: any) {
    this._apiRef.send(message, payload);
  }

  private getCurrentCamera(): Observable<CameraModel> {
    return this.getAPIData(MessageEnum.CameraInfo)
      .pipe(map((data: any) => {
        return <CameraModel>{
          eye: data.position,
          look: data.target,
          up: data.up
        };
      }));
  }

  private getCurrentBGColor(): Observable<BackgroundModel> {
    return this.getAPIData(MessageEnum.UIGetBackground)
      .pipe(
        map((data) => {
          if (data) {
            return new BackgroundModel(
              [...[].concat(...data.colors)], // [[r,g,b,a], [r,g,b,a]] -> [r,g,b,a,r,g,b,a]
              data.gradientType,
              data.colorStops
            );
          } else {
            return null;
          }
        })
      );
  }

  private getCameraUpdates(): Observable<CameraModel> {
    return this.getAPIEvent(EventsEnum.CameraUpdated)
      .pipe(map((data) => {
        const { position, target, up } = data;
        return <CameraModel>{
          eye: position,
          look: target,
          up: up
        };
      }));
  }

  private setUIDisplay(uiDisplay: UiIO.UIDisplayParams) {
    this._apiRef.send(MessageEnum.UISetDisplay, uiDisplay);
  }

  private resetDisplay() {
    this._apiRef.send(MessageEnum.UIResetDisplay);
  }

  private hideUIDisplay() {
    this.setUIDisplay({
      info: false,
      search: false,
      reset: false,
      fullscreen: false,
      nav: false,
      tools: false,
      help: false,
      chapterList: false,
      labelList: false,
      anatomyDescriptions: false,
      tour: false,
      media: false,
      menu: false
    });
  }

  private convertWidgetOptionsToDisplayParams(params: WidgetOptions): UiIO.UIDisplayParams {
    return {
      info: params.uiInfo,
      search: params.uiSearch,
      reset: params.uiReset,
      fullscreen: params.uiFullscreen,
      nav: params.uiNav,
      tools: params.uiTools,
      help: params.uiHelp,
      chapterList: params.uiChapterList,
      labelList: params.uiLabelList,
      anatomyDescriptions: this._params.uiAnatomyDescriptions
    };
  }

  private getScrollCaptureMode(disableScroll: { alert: boolean, overlay: boolean }): ScrollModeEnum {
    if (disableScroll.alert) {
      return ScrollModeEnum.DoNotCaptureShowMessage;
    }
    if (disableScroll.overlay) {
      return ScrollModeEnum.DoNotCapture;
    }
    return ScrollModeEnum.Capture;
  }

  private getLoadingExperience(initial: { button: boolean, handHint: boolean, play: boolean }): LoaderTypeEnum | undefined {
    if (initial.button) {
      return LoaderTypeEnum.Button;
    }
    if (initial.handHint) {
      return LoaderTypeEnum.Hand;
    }
    if (initial.play) {
      return LoaderTypeEnum.Play;
    }
    return undefined;
  }
}
